// frontend/src/components/ArtHouse/ArtHouseFeedPostCard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Heart,
  MessageCircle,
  Bookmark,
  Share2,
  MoreHorizontal,
} from "lucide-react";
import { useArtists } from "../../context/ArtistContext";

const GLASS_TINT = "bg-black/35"; // try bg-black/30, /35, /40

function cx(...parts) {
  return parts.filter(Boolean).join(" ");
}

/* ---------------------------
   Safe helpers
---------------------------- */
function pickFirstNonEmpty(...vals) {
  for (const v of vals) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return null;
}

function isPlaceholderCreator(v) {
  const s = String(v || "").trim().toLowerCase();
  return s === "creator";
}

function pickFirstGood(...vals) {
  for (const v of vals) {
    if (v === undefined || v === null) continue;
    const s = String(v).trim();
    if (!s) continue;
    if (isPlaceholderCreator(s)) continue;
    return v;
  }
  return null;
}

function normalizeImageUrl(post) {
  return (
    post?.imageUrl ||
    post?.image_url ||
    post?.thumbnail_url ||
    post?.thumb_url ||
    post?.cover_url ||
    post?.media_url ||
    ""
  );
}

function normalizeTitle(post) {
  const title = pickFirstNonEmpty(
    post?.title,
    post?.name,
    post?.art_title,
    post?.artTitle
  );
  return title ? String(title) : "";
}

function normalizeDescription(post) {
  const desc = pickFirstNonEmpty(
    post?.description,
    post?.caption,
    post?.subtitle,
    post?.body,
    post?.text
  );
  return desc ? String(desc) : "";
}

function normalizeStats(post) {
  const s = post?.stats || post || {};
  return {
    likes: s?.likes ?? s?.like_count ?? s?.likes_count ?? 0,
    comments: s?.comments ?? s?.comment_count ?? s?.comments_count ?? 0,
    saves: s?.saves ?? s?.save_count ?? s?.saves_count ?? 0,
  };
}

function normalizeViewerState(post) {
  return post?.viewerState || post?.viewer_state || {};
}

function normalizeId(post) {
  return post?.id ?? post?.artwork_id ?? post?.artworkId ?? null;
}

function normalizeArtistSource(post) {
  const a = post?.artist || post?.creator || post?.user || post?.author || {};

  const id =
    a?.id ??
    a?.artist_id ??
    a?.user_id ??
    post?.artist_id ??
    post?.user_id ??
    null;

  const displayName = pickFirstGood(a?.display_name, a?.displayName, a?.name);
  const username = pickFirstGood(
    a?.username,
    a?.user_name,
    a?.userName,
    a?.handle
  );
  const avatar = pickFirstGood(
    a?.avatar_url,
    a?.avatarUrl,
    a?.avatar,
    a?.profile_image_url,
    a?.profileImageUrl
  );

  return {
    id,
    seed: {
      ...(id ? { id } : {}),
      ...(displayName ? { display_name: String(displayName) } : {}),
      ...(username ? { username: String(username).replace(/^@/, "") } : {}),
      ...(avatar ? { avatar_url: String(avatar) } : {}),
    },
    displayName: displayName ? String(displayName) : null,
    username: username ? String(username).replace(/^@/, "") : null,
    avatar: avatar ? String(avatar) : null,
  };
}

function formatTimeAgo(dateLike) {
  if (!dateLike) return "";
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d`;
  const w = Math.floor(days / 7);
  if (w < 4) return `${w}w`;
  const mo = Math.floor(days / 30);
  if (mo < 12) return `${mo}mo`;
  const y = Math.floor(days / 365);
  return `${y}y`;
}

function Avatar({ src, name }) {
  if (src) {
    return (
      <img
        src={src}
        alt={name ? `${name} avatar` : "Avatar"}
        className="
          h-9 w-9 sm:h-10 sm:w-10
          rounded-full object-cover
          border border-white/12 bg-white/5
        "
        loading="lazy"
      />
    );
  }

  return (
    <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full border border-white/12 bg-white/[0.05] flex items-center justify-center text-white/75 text-sm">
      {String(name || "A")
        .trim()
        .slice(0, 1)
        .toUpperCase()}
    </div>
  );
}

/* ---------------------------
   Blurred backdrop from artwork
---------------------------- */
function BlurredImageBg({ src, opacity = 0.7 }) {
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      className="absolute inset-0 h-full w-full object-cover blur-2xl scale-110"
      style={{ opacity }}
      loading="lazy"
    />
  );
}

/* ---------------------------
   Overlay action (icon-only)
   (no boxes, no borders)
---------------------------- */
function OverlayIconButton({
  icon,
  label,
  active,
  onClick,
  disabled,
  activeColorClass = "text-emerald-300",
  count = null,
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      className={cx(
        "inline-flex items-center justify-center gap-1.5",
        count == null ? "h-10 w-10 rounded-full" : "h-10 rounded-full px-3",
        "bg-transparent",
        "text-white/90 hover:text-white",
        "transition-[transform,color] duration-150",
        "active:scale-[0.98] active:translate-y-[0.5px]",
        disabled && "opacity-60 cursor-not-allowed",
        active && activeColorClass
      )}
      aria-pressed={Boolean(active)}
      aria-label={label}
      title={label}
    >
      {icon}
      {count != null ? <span className="text-xs tabular-nums">{count}</span> : null}
    </button>
  );
}

export default function ArtHouseFeedPostCard({
  post,
  onOpen,
  onOpenArtist,
  onLike,
  onSave,
  onComment,
  onShare,
  onMore,
}) {
  const { upsertArtist, getArtist } = useArtists();

  const id = useMemo(() => normalizeId(post), [post]);
  const imageUrl = useMemo(() => normalizeImageUrl(post), [post]);
  const title = useMemo(() => normalizeTitle(post), [post]);
  const description = useMemo(() => normalizeDescription(post), [post]);
  const stats = useMemo(() => normalizeStats(post), [post]);
  const viewerState = useMemo(() => normalizeViewerState(post), [post]);

  const timeAgo = useMemo(() => {
    const created =
      post?.createdAt ??
      post?.created_at ??
      post?.created ??
      post?.created_on ??
      null;
    return formatTimeAgo(created);
  }, [post]);

  const artistSource = useMemo(() => normalizeArtistSource(post), [post]);

  useEffect(() => {
    if (artistSource?.id && artistSource?.seed) {
      upsertArtist(artistSource.seed);
    }
  }, [artistSource?.id, artistSource?.seed, upsertArtist]);

  const liveArtist = useMemo(() => {
    const live = artistSource?.id ? getArtist(artistSource.id) : null;
    return live || post?.artist || post?.creator || {};
  }, [artistSource?.id, getArtist, post]);

  const artistUsername = pickFirstGood(
    liveArtist?.username,
    liveArtist?.user_name,
    liveArtist?.userName,
    liveArtist?.handle,
    artistSource?.username
  );

  const artistDisplayName = pickFirstGood(
    liveArtist?.display_name,
    liveArtist?.displayName,
    liveArtist?.name,
    artistSource?.displayName
  );

  const artistId = useMemo(() => {
    return (
      artistSource?.id ??
      liveArtist?.id ??
      liveArtist?.artist_id ??
      liveArtist?.user_id ??
      post?.artist_id ??
      post?.user_id ??
      null
    );
  }, [
    artistSource?.id,
    liveArtist?.id,
    liveArtist?.artist_id,
    liveArtist?.user_id,
    post?.artist_id,
    post?.user_id,
  ]);

  const headerName = artistUsername || artistDisplayName || "Unknown";
  const headerHandle = artistUsername
    ? String(artistUsername).replace(/^@/, "")
    : null;

  const headerAvatar = pickFirstGood(
    liveArtist?.avatar_url,
    liveArtist?.avatarUrl,
    liveArtist?.avatar,
    liveArtist?.profile_image_url,
    artistSource?.avatar
  );

  const shareUrl = useMemo(() => {
    if (!id) return window.location.origin;
    return `${window.location.origin}/arthouse/art/${id}`;
  }, [id]);

  const [busy, setBusy] = useState({ like: false, save: false, share: false });
  const [shareToast, setShareToast] = useState("");
  const toastTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  const showShareToast = (msg) => {
    setShareToast(msg);
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setShareToast(""), 1400);
  };

  const run = async (key, fn) => {
    if (!fn) return;
    if (busy[key]) return;
    setBusy((s) => ({ ...s, [key]: true }));
    try {
      await fn();
    } finally {
      setBusy((s) => ({ ...s, [key]: false }));
    }
  };

  const defaultShare = async () => {
    const shareTitle = title ? String(title) : "ArtHouse";
    const text = description
      ? String(description).slice(0, 140)
      : "Check this out on AnimeHouse.";

    try {
      if (
        typeof navigator !== "undefined" &&
        typeof navigator.share === "function"
      ) {
        await navigator.share({ title: shareTitle, text, url: shareUrl });
        return;
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        showShareToast("Copied link");
        return;
      }
    } catch {}

    window.prompt("Copy this link:", shareUrl);
    showShareToast("Link ready");
  };

  const handleShare = async () => {
    if (typeof onShare === "function") {
      try {
        const handled = await onShare({ post, url: shareUrl });
        if (handled === true) return;
      } catch {}
    }
    await defaultShare();
  };

  const handleArtistOpen = (e) => {
    e.stopPropagation();
    if (typeof onOpenArtist === "function" && artistId) {
      onOpenArtist({ artistId, post, artist: liveArtist });
      return;
    }
    onOpen?.();
  };

  return (
    <article
      className="
        relative overflow-hidden
        rounded-2xl sm:rounded-3xl
        border border-white/10
        bg-transparent
      "
      style={{
        maxHeight: "min(calc(100svh - 180px), 760px)",
      }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen?.();
          }
        }}
        className="relative block w-full text-left"
        aria-label="Open post"
      >
        <div className="relative w-full h-[540px] sm:h-[640px] md:h-[700px]">
          {/* Full card backdrop is ONLY the artwork (blurred) */}
          <div className="absolute inset-0">
            <BlurredImageBg src={imageUrl} opacity={0.8} />
          </div>

          {/* Main image */}
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={title || "Artwork"}
              className="relative z-10 h-full w-full object-contain"
              loading="lazy"
            />
          ) : (
            <div className="relative z-10 flex h-full w-full items-center justify-center text-sm text-white/60">
              No image
            </div>
          )}

          {/* TOP overlay */}
          <div className="absolute left-3 right-3 top-3 sm:left-4 sm:right-4 sm:top-4 z-20 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={handleArtistOpen}
              className={cx(
                "group flex items-center gap-2.5 sm:gap-3 min-w-0 text-left",
                "backdrop-blur-xl rounded-full px-2.5 py-2",
                "transition hover:bg-white/10",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20",
                GLASS_TINT
              )}
              aria-label="Open profile"
            >
              <Avatar src={headerAvatar} name={headerName} />
              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="truncate text-[13px] sm:text-[14px] font-semibold text-white group-hover:text-white">
                    {headerName}
                  </div>
                  {headerHandle ? (
                    <div className="truncate text-[12px] text-white/70 group-hover:text-white/85">
                      @{headerHandle}
                    </div>
                  ) : null}
                  {timeAgo ? (
                    <div className="shrink-0 text-[12px] text-white/55 group-hover:text-white/75">
                      · {timeAgo}
                    </div>
                  ) : null}
                </div>
                <div className="text-[12px] text-white/65 group-hover:text-white/80">
                  ArtHouse
                </div>
              </div>
            </button>

            <div className={cx("backdrop-blur-xl rounded-full p-1", GLASS_TINT)}>
              <OverlayIconButton
                icon={<MoreHorizontal className="h-5 w-5" />}
                label="More"
                onClick={() => onMore?.()}
              />
            </div>
          </div>

          {/* BOTTOM overlay */}
          <div className="absolute left-3 right-3 bottom-3 sm:left-4 sm:right-4 sm:bottom-4 z-20">
            <div
              className={cx(
                "flex items-end justify-between gap-3",
                "backdrop-blur-xl rounded-2xl px-3 py-2.5",
                GLASS_TINT
              )}
            >
              {/* Counts */}
              <div className="min-w-0">
                <div className="flex items-center gap-3 text-[12px] sm:text-[13px] text-white/90">
                  <span>
                    <span className="font-semibold text-white">
                      {stats?.likes ?? 0}
                    </span>{" "}
                    likes
                  </span>

                  <span className="text-white/40">•</span>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onComment?.();
                    }}
                    className="hover:text-white transition"
                    aria-label="Open comments"
                    title="Open comments"
                  >
                    <span className="font-semibold text-white">
                      {stats?.comments ?? 0}
                    </span>{" "}
                    comments
                  </button>
                </div>

                {shareToast ? (
                  <div className="mt-2">
                    <div className="inline-flex items-center rounded-full px-2.5 py-1 text-[12px] text-white/90 backdrop-blur-xl bg-black/25">
                      {shareToast}
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Actions */}
              <div
                className="flex items-center gap-2"
                onClick={(e) => e.stopPropagation()}
                role="presentation"
              >
                <OverlayIconButton
                  active={Boolean(viewerState?.liked)}
                  disabled={busy.like}
                  onClick={() => run("like", onLike)}
                  activeColorClass="text-emerald-300"
                  label="Like"
                  count={Number(stats?.likes ?? 0)}
                  icon={
                    <Heart
                      className="h-5 w-5"
                      fill={viewerState?.liked ? "currentColor" : "none"}
                    />
                  }
                />

                <OverlayIconButton
                  disabled={false}
                  onClick={onComment}
                  label="Comment"
                  icon={<MessageCircle className="h-5 w-5" />}
                />

                <OverlayIconButton
                  disabled={busy.share}
                  onClick={() => run("share", handleShare)}
                  label="Share"
                  icon={<Share2 className="h-5 w-5" />}
                />

                <OverlayIconButton
                  active={Boolean(viewerState?.saved)}
                  disabled={busy.save}
                  onClick={() => run("save", onSave)}
                  label="Save"
                  icon={
                    <Bookmark
                      className="h-5 w-5"
                      fill={viewerState?.saved ? "currentColor" : "none"}
                    />
                  }
                />
              </div>
            </div>
          </div>

          {/* Optional: click hint */}
          <div className="pointer-events-none absolute inset-x-0 bottom-2 hidden sm:flex justify-center z-10">
            <div className="text-[12px] text-white/55 backdrop-blur-xl rounded-full px-3 py-1 bg-black/25">
              Tap artwork to open
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

